import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import {
  Table,
  message,
  Spin,
  Typography,
  Button,
  Popconfirm,
  Tag,
  Space,
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  EditOutlined,
  DownOutlined,
  RightOutlined,
  TeamOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import { PermissionsContext } from 'util/PermissionsContext';
import * as api from 'util/api';
import PropTypes from 'prop-types';
import RoleModal from './RoleModal';

const { Text } = Typography;

const RoleRouteMapping = ({ routes }) => {
  const { canManageRoles } = useContext(PermissionsContext);

  const [roles, setRoles] = useState([]);
  const [permissionsList, setPermissionsList] = useState([]);
  const [allUserRoles, setAllUserRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingRoleId, setSavingRoleId] = useState(null);

  const [modalVisible, setModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  const [expandedPermissions, setExpandedPermissions] = useState({});
  const [expandedUsers, setExpandedUsers] = useState({});

  const permissionKeyToId = useMemo(
    () => new Map((permissionsList || []).map((p) => [p.key, p.id])),
    [permissionsList],
  );

  const routesWithPermissionId = useMemo(
    () => (routes || [])
      .filter((r) => permissionKeyToId.has(r.permission))
      .map((r) => ({
        ...r,
        permissionId: permissionKeyToId.get(r.permission),
      })),
    [routes, permissionKeyToId],
  );

  const permissionIdToLabel = useMemo(
    () => new Map(routesWithPermissionId.map((r) => [r.permissionId, r.menuLabel])),
    [routesWithPermissionId],
  );

  const usersByRole = useMemo(() => {
    const map = {};
    (allUserRoles || []).forEach((ur) => {
      if (!ur.roleId) return;
      if (!map[ur.roleId]) map[ur.roleId] = [];
      const user = (users || []).find((u) => u.uid === ur.firebaseUid);
      if (user && !map[ur.roleId].some((u) => u.uid === user.uid)) {
        map[ur.roleId].push(user);
      }
    });
    return map;
  }, [allUserRoles, users]);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      api.getRoles(),
      api.getPermissions(),
      api.getUserRoles(),
      api.getUsers(),
    ])
      .then(([rolesData, permsData, userRolesData, usersData]) => {
        setRoles(Array.isArray(rolesData) ? rolesData : []);
        setPermissionsList(Array.isArray(permsData) ? permsData : []);
        setAllUserRoles(Array.isArray(userRolesData) ? userRolesData : []);
        setUsers(Array.isArray(usersData) ? usersData : []);
      })
      .catch(() => message.error('Failed to load roles and permissions'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!canManageRoles) return;
    fetchAll();
  }, [canManageRoles]);

  const handleDeleteRole = (role) => {
    setSavingRoleId(role.id);
    api.deleteRole(role.id)
      .then(() => {
        setRoles((prev) => prev.filter((r) => r.id !== role.id));
        setAllUserRoles((prev) => prev.filter((ur) => ur.roleId !== role.id));
        message.success(`Deleted ${role.name}`);
      })
      .catch(() => message.error('Failed to delete role'))
      .finally(() => setSavingRoleId(null));
  };

  const openCreateModal = () => {
    setEditingRole(null);
    setModalVisible(true);
  };

  const openEditModal = (role) => {
    setEditingRole(role);
    setModalVisible(true);
  };

  const handleModalClose = (didSave) => {
    setModalVisible(false);
    setEditingRole(null);
    if (didSave) fetchAll();
  };

  const togglePermissions = (roleId) => {
    setExpandedPermissions((prev) => ({ ...prev, [roleId]: !prev[roleId] }));
  };

  const toggleUsers = (roleId) => {
    setExpandedUsers((prev) => ({ ...prev, [roleId]: !prev[roleId] }));
  };

  if (!canManageRoles) {
    return (
      <p>
        <Text type="secondary">You need permission to manage roles and routes.</Text>
      </p>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin tip="Loading roles and routes…" />
      </div>
    );
  }

  const totalPermissions = permissionsList.length;

  const columns = [
    {
      title: 'Role',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <span>
          <Text strong>{name}</Text>
          {record.description && (
            <div style={{ fontSize: 12, color: '#999' }}>{record.description}</div>
          )}
        </span>
      ),
    },
    {
      title: 'Permissions',
      key: 'permissionCount',
      width: 160,
      render: (_, record) => {
        const count = (record.permissions || []).length;
        return (
          <Tag color={count > 0 ? 'blue' : undefined}>
            {count}
            /
            {totalPermissions}
            {' '}
            permissions
          </Tag>
        );
      },
    },
    {
      title: 'Users',
      key: 'userCount',
      width: 100,
      render: (_, record) => {
        const count = (usersByRole[record.id] || []).length;
        return (
          <Tag color={count > 0 ? 'green' : undefined}>
            {count}
            {' '}
            {count === 1 ? 'user' : 'users'}
          </Tag>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 240,
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
            disabled={savingRoleId === record.id}
          >
            Edit
          </Button>
          <Button
            size="small"
            icon={expandedPermissions[record.id] ? <DownOutlined /> : <RightOutlined />}
            onClick={() => togglePermissions(record.id)}
          >
            <UnlockOutlined />
          </Button>
          <Button
            size="small"
            icon={expandedUsers[record.id] ? <DownOutlined /> : <RightOutlined />}
            onClick={() => toggleUsers(record.id)}
          >
            <TeamOutlined />
          </Button>
          <Popconfirm
            title={`Delete role "${record.name}"?`}
            description="This will also remove the role from all users."
            onConfirm={() => handleDeleteRole(record)}
            okText="Delete"
            okType="danger"
            cancelText="Cancel"
          >
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={savingRoleId === record.id}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const renderExpandedRow = (record) => {
    const showPerms = expandedPermissions[record.id];
    const showUsers = expandedUsers[record.id];

    if (!showPerms && !showUsers) return null;

    return (
      <div style={{ padding: '4px 0 4px 24px' }}>
        {showPerms && (
          <div style={{ marginBottom: showUsers ? 12 : 0 }}>
            <Text strong style={{ fontSize: 13 }}>Permissions:</Text>
            <div style={{
              marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4,
            }}
            >
              {(record.permissions || []).length === 0 ? (
                <Text type="secondary" style={{ fontSize: 13 }}>No permissions assigned</Text>
              ) : (
                (record.permissions || []).map((p) => (
                  <Tag key={p.id}>{permissionIdToLabel.get(p.id) || p.key}</Tag>
                ))
              )}
            </div>
          </div>
        )}
        {showUsers && (
          <div>
            <Text strong style={{ fontSize: 13 }}>Users:</Text>
            <div style={{
              marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4,
            }}
            >
              {(usersByRole[record.id] || []).length === 0 ? (
                <Text type="secondary" style={{ fontSize: 13 }}>No users assigned</Text>
              ) : (
                (usersByRole[record.id] || []).map((u) => (
                  <Tag key={u.uid}>{u.name || u.email}</Tag>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const expandedRowKeys = roles
    .filter((r) => expandedPermissions[r.id] || expandedUsers[r.id])
    .map((r) => r.id);

  return (
    <div style={{ padding: '0 0 24px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
      }}
      >
        <Text type="secondary">
          Create roles that group permissions together, then assign them to users.
        </Text>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreateModal}
        >
          Create role
        </Button>
      </div>
      <Table
        rowKey="id"
        dataSource={roles}
        columns={columns}
        pagination={false}
        size="small"
        expandable={{
          expandedRowRender: renderExpandedRow,
          expandedRowKeys,
          showExpandColumn: false,
        }}
      />
      <RoleModal
        visible={modalVisible}
        onClose={handleModalClose}
        existingRole={editingRole}
        routes={routesWithPermissionId}
        permissionsList={permissionsList}
        users={users}
      />
    </div>
  );
};

RoleRouteMapping.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.shape({
    menuLabel: PropTypes.string,
    permission: PropTypes.string,
    id: PropTypes.string,
  })),
};

RoleRouteMapping.defaultProps = {
  routes: [],
};

export default RoleRouteMapping;
